/* Sticky add-to-cart bar: appears after the main button scrolls out of view, keeps its own variant pickers in sync. */
(function () {
  if (customElements.get('sticky-atc')) return;

  class StickyAtc extends HTMLElement {
    connectedCallback() {
      // Live directly under <body>: the theme's parallax wrapper is transformed, which would turn
      // position: fixed into an offset inside the page instead of the viewport.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
        return;
      }

      try {
        this.variants = JSON.parse(this.querySelector('[data-sticky-variants]')?.textContent || '[]');
      } catch (error) {
        console.warn('sticky-atc: variants JSON could not be parsed', error);
        this.variants = [];
      }

      this.idInput = this.querySelector('[data-sticky-variant-id]');
      this.selects = Array.from(this.querySelectorAll('[data-sticky-option]'));
      this.price = this.querySelector('[data-sticky-price]');
      this.compare = this.querySelector('[data-sticky-compare]');
      this.save = this.querySelector('[data-sticky-save]');
      this.saveText = this.querySelector('[data-sticky-save-text]');
      this.image = this.querySelector('[data-sticky-image]');
      this.button = this.querySelector('[data-sticky-button]');
      this.buttonText = this.querySelector('[data-sticky-button-text]');
      this.section = document.getElementById(`productTemplate--${this.dataset.sectionId}`) || document;

      if (!this.idInput || !this.button || !this.buttonText) return;

      this.selects.forEach((select) => select.addEventListener('change', () => this.onSelectChange()));
      this.onVariantChange = (event) => this.syncFromVariant(event.detail && event.detail.variant);
      document.addEventListener('theme:variant:change', this.onVariantChange);

      this.observeMainButton();
    }

    disconnectedCallback() {
      document.removeEventListener('theme:variant:change', this.onVariantChange);
      if (this.observer) this.observer.disconnect();
      document.body.classList.remove('has-sticky-atc');
    }

    // The bar shows once the shopper scrolls past the sentinel: the first visible add-to-cart button,
    // or, when an app block (e.g. a bundle builder) hides the theme's button, that app block itself.
    pickSentinel() {
      const isVisible = (el) => el && el.offsetParent !== null;
      const button = Array.from(this.section.querySelectorAll('[data-add-to-cart]')).find((el) => !this.contains(el) && isVisible(el));
      if (button) return button;
      return this.section.querySelector('.product__block.standard__app, [data-form-wrapper], .product-single__details');
    }

    observeMainButton() {
      const target = this.pickSentinel();
      if (!target || !('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          this.toggle(scrolledPast);
        });
      }, { threshold: 0 });
      this.observer.observe(target);
    }

    toggle(show) {
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.body.classList.toggle('has-sticky-atc', show);
    }

    onSelectChange() {
      const chosen = this.selects.map((select) => select.value);
      const variant = this.variants.find((candidate) => chosen.every((value, index) => candidate[`option${index + 1}`] === value));
      this.apply(variant);
    }

    syncFromVariant(variant) {
      if (!variant) return;
      const match = this.variants.find((candidate) => candidate.id === variant.id);
      if (!match) return;
      this.selects.forEach((select, index) => {
        select.value = match[`option${index + 1}`];
      });
      this.apply(match);
    }

    apply(variant) {
      if (!variant) {
        this.button.setAttribute('disabled', 'disabled');
        this.buttonText.textContent = (window.theme && window.theme.strings && window.theme.strings.unavailable) || 'Unavailable';
        return;
      }

      this.idInput.value = variant.id;
      this.price.textContent = this.money(variant.price);

      const saving = variant.compare_at_price && variant.compare_at_price > variant.price ? variant.compare_at_price - variant.price : 0;
      if (this.compare) {
        this.compare.textContent = saving ? this.money(variant.compare_at_price) : '';
        this.compare.classList.toggle('hidden', !saving);
      }
      if (this.save) this.save.classList.toggle('hidden', !saving);
      if (this.saveText && saving) this.saveText.textContent = `SAVE ${this.money(saving, true)}`;

      if (this.image && variant.featured_image && variant.featured_image.src) {
        const src = variant.featured_image.src;
        this.image.src = `${src}${src.includes('?') ? '&' : '?'}width=120&height=120&crop=center`;
      }

      this.button.toggleAttribute('disabled', !variant.available);
      this.buttonText.textContent = variant.available ? this.button.dataset.addLabel : this.button.dataset.soldOutLabel;
    }

    money(cents, trimZeros) {
      const theme = window.theme || {};
      if (typeof theme.formatMoney === 'function') {
        const formatted = theme.formatMoney(cents, theme.moneyFormat || '${{amount}}');
        return trimZeros ? formatted.replace(/[.,]00(?=\D*$)/, '') : formatted;
      }
      return `$${(cents / 100).toFixed(trimZeros ? 0 : 2)}`;
    }
  }

  customElements.define('sticky-atc', StickyAtc);
})();
